import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CheckIcon, ChevronsUpDownIcon, MonitorIcon, PlusIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "../components/ui/button"
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover"
import { fetchExercisesQueryOptions, useExercises } from "../hooks/useExercises"
import { cn } from "../lib/utils"
import { apiFetch } from "../utils/apiFetch"
import { wordMatch } from "../utils/workoutUtils"
import { Skeleton } from "./ui/skeleton"

export type TPartialExercise = {
  exerciseId: string
  name: string
  isPublic: boolean
}

interface IProps {
  value: string
  onValueChange: (value: string) => void
  className?: string
  placeholder?: string
}

export default function ExercisesDropdown({
  value,
  onValueChange,
  className,
  placeholder = "Select or create an exercise...",
}: IProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [localExercises, setLocalExercises] = useState<Array<TPartialExercise>>([])

  const { data: exercises, isLoading } = useExercises()

  const { mutate: addExercise } = useMutation({
    mutationFn: async (exerciseName: string) => {
      const exerciseId = crypto.randomUUID()
      const response = await apiFetch<TPartialExercise>("/v1/exercises", {
        method: "POST",
        body: JSON.stringify({ name: exerciseName, exerciseId: exerciseId }),
      })
      return response
    },
    onSuccess: (_newExercise: TPartialExercise) => {
      queryClient.refetchQueries({
        queryKey: fetchExercisesQueryOptions.queryKey,
      })
    },
    onError: (error) => {
      console.error(error)
    },
  })

  useEffect(() => {
    if (exercises) {
      setLocalExercises(
        exercises.map((ex) => ({
          exerciseId: ex.exerciseId || crypto.randomUUID(),
          name: ex.name,
          isPublic: ex.isPublic || false,
        }))
      )
    }
  }, [exercises])

  const filtered = localExercises.filter((ex) => wordMatch(inputValue, ex.name))

  const exactMatch = localExercises.some((ex) => ex.name.toLowerCase() === inputValue.toLowerCase())

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="exercise-dropdown"
          className={`flex-1 justify-between font-medium ${className}`}
        >
          {value || placeholder}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={`p-0 sm:w-[250px] md:w-[400px] lg:w-[450px] xl:w-[500px]`}>
        {isLoading ? (
          <div className="p-4">
            <Skeleton className="h-5 w-full" />
          </div>
        ) : (
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search or add..." onValueChange={setInputValue} />
            <CommandList>
              <CommandGroup>
                {filtered.map((exercise) => (
                  <CommandItem
                    key={exercise.name}
                    value={exercise.name}
                    onSelect={(currentValue) => {
                      onValueChange(currentValue)
                      setOpen(false)
                    }}
                  >
                    <CheckIcon className={cn("mr-2 h-4 w-4", value === exercise.name ? "opacity-100" : "opacity-0")} />
                    <div className="flex w-full items-center justify-between">
                      {exercise.name}
                      {exercise.isPublic && <MonitorIcon className="ml-2 h-4 w-4 opacity-50" />}
                    </div>
                  </CommandItem>
                ))}

                {/* Show create option only if input is non-empty and doesn't exactly match */}
                {!exactMatch && inputValue.trim() !== "" && (
                  <CommandItem
                    key="_create_new"
                    value={inputValue}
                    onSelect={() => {
                      //TODO: Make an API call that creates the new exercise on the server
                      //as well as adding it to the local state
                      addExercise(inputValue)
                      setLocalExercises((prev) => [
                        ...prev,
                        {
                          name: inputValue,
                          exerciseId: crypto.randomUUID(),
                          isPublic: false,
                        },
                      ])
                      onValueChange(inputValue)
                      setInputValue("")
                      setOpen(false)
                    }}
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    Create new: "{inputValue}"
                  </CommandItem>
                )}

                {filtered.length === 0 && !exactMatch && inputValue.trim() !== "" && (
                  <div className="p-2 text-muted-foreground text-sm">No matches found.</div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}
